import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, PackageOpen, PackageMinus, ShieldAlert,
  Truck, Warehouse, Users, LogOut, X, Building2, Boxes,
  ShieldCheck, ToggleRight, Tags, LayoutGrid, PauseCircle, BarChart3,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logo from '../../ZIPLOGYX-Logo-Link.png';

// Operational nav — each item is gated by a permission + (optional) feature module.
const MAIN_NAV = [
  { label: 'Dashboard',        path: '/dashboard',       icon: LayoutDashboard, permission: 'dashboard.view',        module: 'dashboard' },
  { label: 'Inward',           path: '/inward',          icon: PackageOpen,     permission: 'inward.view',           module: 'inward' },
  { label: 'GRN',              path: '/grn',             icon: Boxes,           permission: 'grn.view',              module: 'grn' },
  { label: 'Quarantine',       path: '/quarantine',      icon: ShieldAlert,     permission: 'quarantine.view',       module: 'quarantine' },
  { label: 'Rack Storage',     path: '/rack',            icon: Warehouse,       permission: 'rack.view',             module: 'rack' },
  { label: 'Storage Setup',    path: '/storage',         icon: LayoutGrid,      permission: 'storage.view',          module: 'storage' },
  { label: 'Outward Dispatch', path: '/dispatch',        icon: PackageMinus,    permission: 'dispatch.view',         module: 'dispatch' },
  { label: 'Dispatch Hold',    path: '/dispatch-hold',   icon: PauseCircle,     permission: 'dispatch_hold.view',    module: 'dispatch_hold' },
  { label: 'Direct Delivery',  path: '/direct-delivery', icon: Truck,           permission: 'direct_delivery.view',  module: 'direct_delivery' },
  { label: 'Reports & Analytics', path: '/reports',      icon: BarChart3,       permission: 'reports.view',          module: 'reports' },
  { label: 'Products',         path: '/products',        icon: Tags,            permission: 'products.view',         module: 'products' },
  { label: 'Suppliers',        path: '/suppliers',       icon: Truck,           permission: 'suppliers.view',        module: 'suppliers' },
  { label: 'Warehouses',       path: '/warehouses',      icon: Building2,       permission: 'warehouses.view',       module: 'warehouses' },
  { label: 'Clients',          path: '/clients',         icon: Users,           permission: 'clients.view',          module: 'clients' },
  { label: 'User Management',  path: '/users',           icon: Users,           permission: 'users.view',            module: 'users' },
];

// Super Admin section
const ADMIN_NAV = [
  { label: 'Roles & Permissions', path: '/admin/roles',   icon: ShieldCheck,  permission: 'settings.manage' },
  { label: 'Modules & Features',  path: '/admin/modules', icon: ToggleRight,  permission: 'settings.manage' },
];

// Client portal nav (client_user only)
const PORTAL_NAV = [
  { label: 'My Inventory', path: '/portal',            icon: Boxes },
  { label: 'Inbound',      path: '/portal/inbound',    icon: PackageOpen },
  { label: 'Dispatches',   path: '/portal/dispatches', icon: PackageMinus },
];

const roleLabels = {
  super_admin:        'Super Admin',
  admin:              'Administrator',
  store_executive:    'Store Executive',
  dispatch_executive: 'Dispatch Executive',
  client_user:        'Client Portal',
};

const ScanLine = () => (
  <span
    aria-hidden="true"
    className="absolute inset-y-0 left-0 w-full h-px top-1/2 animate-scan-pulse pointer-events-none"
    style={{ background: '#E8192C' }}
  />
);

const NavItem = ({ label, path, icon: Icon, onClose }) => (
  <NavLink
    to={path}
    onClick={onClose}
    className={({ isActive }) =>
      `relative flex items-center gap-3 px-3 h-11 rounded-lg text-sm font-semibold overflow-hidden transition-all duration-150 group focus-ring
      ${isActive
        ? 'bg-zip-red/8 text-zip-text border-l-[3px] border-zip-red pl-[calc(0.75rem-3px)]'
        : 'text-zip-muted hover:bg-zip-hover hover:text-zip-text border-l-[3px] border-transparent'
      }`
    }
  >
    {({ isActive }) => (
      <>
        {isActive && <ScanLine />}
        <Icon size={16} className={`flex-shrink-0 transition-all duration-150 ${isActive ? 'text-zip-red' : 'text-zip-muted group-hover:text-zip-text'}`} />
        <span className="tracking-tight">{label}</span>
      </>
    )}
  </NavLink>
);

const Sidebar = ({ onClose }) => {
  const { user, logout, can, featureEnabled } = useAuth();
  const initials = user?.name?.[0]?.toUpperCase() ?? '?';

  const isClientUser = user?.role === 'client_user';
  const visible = (item) =>
    can(item.permission) && (!item.module || featureEnabled(item.module));

  const mainItems  = isClientUser ? PORTAL_NAV : MAIN_NAV.filter(visible);
  const adminItems = isClientUser ? [] : ADMIN_NAV.filter(visible);

  return (
    <div className="flex flex-col h-full w-60 bg-zip-surface select-none">
      {/* Logo row */}
      <div className="flex items-center justify-between px-5 py-5 flex-shrink-0">
        <img src={logo} alt="Ziplogyx IMS" className="h-10 w-auto object-contain" />
        {onClose && (
          <button onClick={onClose} className="text-zip-muted hover:text-zip-text p-1 focus-ring rounded">
            <X size={18} />
          </button>
        )}
      </div>

      {/* Role badge */}
      <div className="px-5 pb-4 flex-shrink-0">
        <span className="text-[11px] font-semibold font-mono uppercase tracking-widest px-2.5 py-1 rounded-full bg-zip-red/20 text-zip-red border border-zip-red/30">
          {roleLabels[user?.role] ?? user?.roleName ?? 'User'}
        </span>
      </div>

      <div className="mx-4 h-px bg-zip-border flex-shrink-0" />

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        {mainItems.map((item) => <NavItem key={item.path} {...item} onClose={onClose} />)}

        {adminItems.length > 0 && (
          <>
            <div className="px-3 pt-4 pb-1.5">
              <span className="text-[10px] font-semibold font-mono uppercase tracking-widest text-zip-muted/60">
                Access Control
              </span>
            </div>
            {adminItems.map((item) => <NavItem key={item.path} {...item} onClose={onClose} />)}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0">
        <div className="mx-4 h-px bg-zip-border" />
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-3 px-1">
            <div className="w-8 h-8 rounded-full bg-zip-red/20 border border-zip-red/30 flex items-center justify-center flex-shrink-0">
              <span className="text-zip-red text-xs font-bold font-mono">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-zip-text text-sm font-semibold truncate tracking-tight">{user?.name}</div>
              <div className="text-zip-muted text-xs truncate font-mono">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 text-zip-muted hover:text-zip-red hover:bg-red-50 rounded-lg text-sm font-medium transition-all duration-150 focus-ring group"
          >
            <LogOut size={14} className="group-hover:text-zip-red transition-colors" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
